import { scopesByOperationId } from '@msi-alta/helium-client/scopes';

import { validateScopes, getActiveScopes } from 'utils/scopes';
import { getAccessToken } from 'utils/accessToken';
import { getOpParams } from 'utils/getOpParams';

import { useOpQuery } from './useOpQuery';

/**
 * Custom hook to check if the current user has permission to access specific API endpoints.
 * It validates the user's scopes against the required scopes for the specified endpoints.
 *
 * @param endpoint - The API endpoint(s) to check permissions for. Can be a single endpoint or an array of endpoints.
 *                   Each endpoint should be a key in the scopesByOperationId object.
 * @param allowOReadAsWrite - If true, users with organization-level read permissions ('o:r') will be given faux write access.
 *                            This can be used to give internal users access to portions of the UI
 *                            that they wouldn't otherwise have access to.
 * @returns An object containing:
 *   - `isLoading`: A boolean indicating whether the data required for permission checking is still loading.
 *   - `hasReadPermission`: A boolean indicating whether the user has read permission for all specified endpoints.
 *   - `hasWritePermission`: A boolean indicating whether the user has write permission for all specified endpoints.
 */
export const useEndpointPermissions = (
  endpoint:
    | keyof typeof scopesByOperationId
    | (keyof typeof scopesByOperationId)[],
  allowOReadAsWrite = true, // We want internal users to have access to all endpoints
) => {
  const { orgId } = getOpParams();
  const accessToken = getAccessToken();

  const { data: orgData, isPending: isOrgIdPending } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data,
  });

  const { data: tokenData, isPending: isTokenPending } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    select: (data) => data.json.data,
  });

  const tokenScopeList = tokenData?.tokenScopeList ?? [];
  const identityId = tokenData?.identity?.id;

  // Get org & identity scopes
  const activeScopes = getActiveScopes({
    tokenScopeList,
    orgId,
    parentOrgId: orgData?.parentOrg?.id,
  });

  // If the endpoint is not an array, wrap it in an array
  const endpoints = Array.isArray(endpoint) ? endpoint : [endpoint];

  const allowedReadScopesForEndpoints = endpoints.map(
    // Write scopes also give read permission so no need to filter
    (ep) => scopesByOperationId[ep] || [],
  );
  const allowedWriteScopesForEndpoints = endpoints.map(
    (ep) => scopesByOperationId[ep]?.filter((s) => s.includes(':w')) || [],
  );

  return {
    isLoading: isOrgIdPending || isTokenPending,
    hasReadPermission: allowedReadScopesForEndpoints.every(
      (allowedReadScopes) =>
        validateScopes({
          activeScopes,
          rawAllowedScopes: allowedReadScopes,
          orgId,
          identityId,
        }),
    ),
    hasWritePermission:
      // This allows internal users to have faux write permissions for UI purposes
      (allowOReadAsWrite && activeScopes.includes('o:r')) ||
      // If allowedWriteScopes is empty, we need to assume the user has access. Regression potentially exposed in https://github.com/msi-alta/platinum/commit/0b92d3d5cc6b9b25e98de73f36964d9f402fb8f6
      !allowedWriteScopesForEndpoints.length ||
      allowedWriteScopesForEndpoints.every((allowedWriteScopes) =>
        validateScopes({
          activeScopes,
          rawAllowedScopes: allowedWriteScopes,
          orgId,
          identityId,
        }),
      ),
  };
};
